import { forwardRef, useId } from "react";
import { Link, useLocation } from "react-router-dom";
import { audioManager } from "../../audio/AudioManager";
import { homeSectionNavigation, siteIcon } from "../../content/siteContent";
import { cn } from "../../lib/cn";
import { useAppStore } from "../../store/useAppStore";

type SiteNavigationProps = {
  hidden?: boolean;
  onNavigateSection: (id: (typeof homeSectionNavigation)[number]["id"]) => void;
};

export const SiteNavigation = forwardRef<HTMLElement, SiteNavigationProps>(function SiteNavigation({ hidden = false, onNavigateSection }, ref) {
  const location = useLocation();
  const menuId = useId();
  const navOpen = useAppStore((state) => state.navOpen);
  const setNavOpen = useAppStore((state) => state.setNavOpen);
  const audioEnabled = useAppStore((state) => state.audioEnabled);
  const setAudioEnabled = useAppStore((state) => state.setAudioEnabled);
  const setAudioUnlocked = useAppStore((state) => state.setAudioUnlocked);
  const currentSection = useAppStore((state) => state.currentSection);
  const isTransitioning = useAppStore((state) => state.isTransitioning);
  const targetSection = useAppStore((state) => state.targetSection);
  const primaryLinks = homeSectionNavigation.filter((item) => item.id !== "contact");
  const contactLink = homeSectionNavigation.find((item) => item.id === "contact");

  const isHome = location.pathname === "/";
  const activeSection = isTransitioning ? targetSection : currentSection;
  const audioLabel = audioEnabled ? "Turn sound off" : "Turn sound on";

  const handleAudioToggle = () => {
    const nextEnabled = !audioEnabled;
    setAudioEnabled(nextEnabled);

    if (!nextEnabled) {
      audioManager.setEnabled(false);
      return;
    }

    audioManager.setEnabled(true);
    void audioManager.unlock().then((unlocked) => {
      if (unlocked) {
        setAudioUnlocked(true);
        audioManager.playSfx("button");
      }
    });
  };

  return (
    <header className={cn("site-nav", hidden && !navOpen && "site-nav--hidden")} ref={ref}>
      <div className="site-nav__inner">
        <Link className="site-brand" to="/">
          <span className="site-brand__mark">
            <img alt="" className="site-brand__logo" height="40" loading="eager" src={siteIcon} width="40" />
          </span>
          <span className="site-brand__copy">
            <strong>7 Stones</strong>
            {/* <small>Web-first game worlds</small> */}
          </span>
        </Link>

        <nav aria-label="Primary navigation" className="site-nav__links">
          {primaryLinks.map((item) => (
            <Link
              className={cn("site-nav__link", isHome && activeSection === item.id && "site-nav__link--active")}
              data-audio-nav="true"
              key={item.id}
              onClick={(event) => {
                if (!isHome) {
                  return;
                }

                event.preventDefault();
                onNavigateSection(item.id);
              }}
              to={item.href}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="site-nav__actions">
          <button
            aria-label={audioLabel}
            aria-pressed={audioEnabled}
            className={cn("site-nav__audio", audioEnabled && "site-nav__audio--active")}
            data-audio-toggle="true"
            onClick={handleAudioToggle}
            type="button"
          >
            <span aria-hidden="true" className="site-nav__audioDot" />
            <span aria-hidden="true" className="site-nav__audioWave site-nav__audioWave--one" />
            <span aria-hidden="true" className="site-nav__audioWave site-nav__audioWave--two" />
          </button>

          {contactLink ? (
            <Link
              className={cn("site-nav__contact", isHome && activeSection === contactLink.id && "site-nav__contact--active")}
              data-audio-nav="true"
              onClick={(event) => {
                if (!isHome) {
                  return;
                }

                event.preventDefault();
                onNavigateSection(contactLink.id);
              }}
              to={contactLink.href}
            >
              {contactLink.label}
            </Link>
          ) : null}

          <button
            aria-controls={menuId}
            aria-expanded={navOpen}
            className="site-nav__toggle"
            onClick={() => setNavOpen(!navOpen)}
            type="button"
          >
            <span className="sr-only">{navOpen ? "Close navigation" : "Open navigation"}</span>
            <span className="site-nav__toggleBars" />
          </button>
        </div>

        <div className={cn("site-nav__mobile", navOpen && "site-nav__mobile--open")} id={menuId}>
          <nav aria-label="Mobile navigation" className="site-nav__mobileList">
            {homeSectionNavigation.map((item) => (
              <Link
                className={cn("site-nav__mobileLink", isHome && activeSection === item.id && "site-nav__mobileLink--active")}
                data-audio-nav="true"
                key={item.id}
                onClick={(event) => {
                  setNavOpen(false);
                  if (!isHome) {
                    return;
                  }

                  event.preventDefault();
                  onNavigateSection(item.id);
                }}
                to={item.href}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
});
